import type { MessageKey } from '../i18n';

export interface NavItem {
  to: string;
  labelKey: MessageKey;
  /** Entry is shown only to users holding this permission (the API enforces it regardless). */
  permission?: string;
  /** With a school-scoped grant the entry is shown too. */
  anyScope?: boolean;
  /** Any one of these also grants the entry (e.g. teachers hold view_assigned instead of view). */
  alsoPermissions?: string[];
}

/** Sidebar entries. Later phases append module routes here. */
export const navItems: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard' },
  { to: '/inbox', labelKey: 'nav.inbox' },
  { to: '/notices', labelKey: 'nav.notices', permission: 'comms.view', anyScope: true },
  {
    to: '/documents',
    labelKey: 'nav.documents',
    permission: 'documents.view',
    anyScope: true,
    alsoPermissions: ['documents.staff_view'],
  },
  {
    to: '/certificates',
    labelKey: 'nav.certificates',
    permission: 'certificates.view',
    anyScope: true,
  },
  { to: '/reports', labelKey: 'nav.reports', permission: 'reports.view', anyScope: true },
  { to: '/my-children', labelKey: 'nav.myChildren', permission: 'portal.parent' },
  {
    to: '/homework',
    labelKey: 'nav.homework',
    permission: 'homework.view_all',
    anyScope: true,
    alsoPermissions: ['homework.manage_all', 'homework.manage'],
  },
  { to: '/messages', labelKey: 'nav.messages', permission: 'messages.use' },
  { to: '/my-classes', labelKey: 'nav.myClasses', permission: 'students.view_assigned' },
  {
    to: '/students',
    labelKey: 'nav.students',
    permission: 'students.view',
    anyScope: true,
    alsoPermissions: ['students.view_assigned'],
  },
  {
    to: '/attendance',
    labelKey: 'nav.attendance',
    permission: 'attendance.view',
    anyScope: true,
    alsoPermissions: ['attendance.view_assigned'],
  },
  {
    to: '/library',
    labelKey: 'nav.library',
    permission: 'library.view',
    anyScope: true,
    alsoPermissions: ['library.view_loans'],
  },
  { to: '/my-library', labelKey: 'nav.myLibrary', permission: 'library.view_own' },
  { to: '/transport', labelKey: 'nav.transport', permission: 'transport.view', anyScope: true },
  { to: '/hostel', labelKey: 'nav.hostel', permission: 'hostel.view', anyScope: true },
  { to: '/inventory', labelKey: 'nav.inventory', permission: 'inventory.view', anyScope: true },
  {
    to: '/hr',
    labelKey: 'nav.hr',
    permission: 'payroll.view',
    anyScope: true,
    alsoPermissions: ['staff_attendance.view', 'hr_leave.view', 'salary.view'],
  },
  {
    to: '/my-hr',
    labelKey: 'nav.myHr',
    permission: 'hr_leave.apply',
    alsoPermissions: ['payslips.view_own'],
  },
  {
    to: '/fees',
    labelKey: 'nav.fees',
    permission: 'fees.view',
    anyScope: true,
    alsoPermissions: ['payments.view'],
  },
  { to: '/accounting', labelKey: 'nav.accounting', permission: 'accounting.view', anyScope: true },
  { to: '/exams', labelKey: 'nav.exams', permission: 'exams.view', anyScope: true },
  {
    to: '/marks',
    labelKey: 'nav.marks',
    permission: 'marks.view',
    anyScope: true,
    alsoPermissions: ['marks.view_assigned'],
  },
  { to: '/timetable', labelKey: 'nav.timetable', permission: 'timetable.view', anyScope: true },
  { to: '/my-timetable', labelKey: 'nav.myTimetable', permission: 'students.view_assigned' },
  {
    to: '/leave-requests',
    labelKey: 'nav.leave',
    permission: 'leave_requests.view',
    anyScope: true,
  },
  { to: '/academics', labelKey: 'nav.academics', permission: 'classes.view', anyScope: true },
  { to: '/admissions', labelKey: 'nav.admissions', permission: 'admissions.view', anyScope: true },
  { to: '/staff', labelKey: 'nav.staff', permission: 'staff.view', anyScope: true },
  { to: '/users', labelKey: 'nav.users', permission: 'users.view' },
  { to: '/schools', labelKey: 'nav.schools', permission: 'schools.view', anyScope: true },
  { to: '/settings', labelKey: 'nav.settings', permission: 'settings.view', anyScope: true },
  { to: '/organization', labelKey: 'nav.organization', permission: 'orgs.view' },
  { to: '/audit', labelKey: 'nav.audit', permission: 'audit.view' },
];

/** The entries a user may see: the same rule for the sidebar and the home screen. */
export function visibleNavItems(
  can: (permission: string) => boolean,
  canAny: (permission: string) => boolean,
): NavItem[] {
  return navItems.filter(
    (item) =>
      !item.permission ||
      [item.permission, ...(item.alsoPermissions ?? [])].some((code) =>
        item.anyScope ? canAny(code) : can(code),
      ),
  );
}
